import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { tradesService } from '@/services';
import { queryKeys } from '@/services/query-keys';
import type { PositionsSummarySection, TradeListParams } from '@/types';

/**
 * LR-546: whole-book blotter metrics (`GET /positions/metrics/`) — per-CP,
 * per-currency Total Value / Exposure subtotals + counts for the MetricsBar and
 * tab badges. Replaces deriving those from the full in-memory book. Honours the
 * same filters + `search` as the list (ordering is irrelevant for aggregates).
 * `section` is the part the caller reads: the server sums only that one.
 */
export const useTradesSummary = <S extends PositionsSummarySection>(
  section: S,
  params?: Omit<TradeListParams, 'limit' | 'offset' | 'ordering'>,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: queryKeys.trades.summary(section, params),
    queryFn: () => tradesService.getPositionsMetrics(section, params),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
    // Keep the prior aggregate visible while a param change (any sidebar filter /
    // search) refetches, so the MetricsBar money cells and the LR-469 footer don't
    // blink empty for a network RTT (kvi-r6 #4). Sorting is excluded from the footer
    // params, so it never triggers this refetch.
    placeholderData: keepPreviousData,
    enabled,
  });
};
